import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, Trash2, AlertTriangle, Calendar, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import RDWasteItemCard from '../components/rd/RDWasteItemCard';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';

interface RDWasteItem {
  id: string;
  user_id: string;
  item_id: string;
  original_project_name: string;
  description: string;
  category: 'broken' | 'discontinued' | 'abandoned' | 'obsolete';
  category_id: string;
  date_added: string;
  reason: string;
  original_cost: number;
  disposal_status: 'pending' | 'scheduled' | 'completed';
  salvage_value: number;
  researcher: string;
  project_team: string;
  storage_location: string;
  project_code: string;
  disposal_method: string;
  environmental_impact: string;
  disposal_date: string;
  disposal_company: string;
  disposal_cost: number;
  images: string[];
  stock_quantity: number;
  created_at: string;
  updated_at: string;
}

const RDWasteManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [rdWasteItems, setRdWasteItems] = useState<RDWasteItem[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [disposalFilter, setDisposalFilter] = useState('');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [lastUpdated] = useState(new Date().toLocaleString());
  
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load R&D waste items from Supabase
      const { data: rdWasteData, error: rdWasteError } = await supabase
        .from('rd_waste_items')
        .select('*')
        .order('created_at', { ascending: false });

      if (rdWasteError) throw rdWasteError;
      setRdWasteItems(rdWasteData || []);

      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading R&D waste items:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setStatusFilter(filter);
      setIsFiltersVisible(true);
    }
  }, [searchParams]);
  
  // Filter waste items based on search and filters
  const filteredItems = rdWasteItems.filter(item => {
    const matchesSearch = 
      item.original_project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.project_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.researcher.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !categoryFilter || item.category_id === categoryFilter;
    
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'broken' && item.category === 'broken') ||
      (statusFilter === 'discontinued' && item.category === 'discontinued') ||
      (statusFilter === 'abandoned' && item.category === 'abandoned') ||
      (statusFilter === 'obsolete' && item.category === 'obsolete');
    
    const matchesDisposal = !disposalFilter || item.disposal_status === disposalFilter;
    
    return matchesSearch && matchesCategory && matchesStatus && matchesDisposal;
  });
  
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSearchParams(params => {
      if (value === 'all') {
        params.delete('filter');
      } else {
        params.set('filter', value);
      }
      return params;
    });
  };
  
  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    })),
  ];
  
  const statusOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'broken', label: 'Broken/Damaged' },
    { value: 'discontinued', label: 'Discontinued' },
    { value: 'abandoned', label: 'Abandoned Research' },
    { value: 'obsolete', label: 'Obsolete Technology' },
  ];

  const disposalOptions = [
    { value: '', label: 'All Disposal Status' },
    { value: 'pending', label: 'Pending Disposal' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
  ];

  // Calculate summary statistics
  const totalItems = rdWasteItems.length;
  const brokenItems = rdWasteItems.filter(item => item.category === 'broken').length;
  const discontinuedItems = rdWasteItems.filter(item => item.category === 'discontinued').length;
  const abandonedItems = rdWasteItems.filter(item => item.category === 'abandoned').length;
  const obsoleteItems = rdWasteItems.filter(item => item.category === 'obsolete').length;
  const totalOriginalCost = rdWasteItems.reduce((sum, item) => sum + item.original_cost, 0);
  const totalSalvageValue = rdWasteItems.reduce((sum, item) => sum + item.salvage_value, 0);
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">R&D Waste Management</h1>
          <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
        </div>
        <Link to="/rd-waste/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Waste Item
          </Button>
        </Link>
      </div>

      {/* Hero Image Section */}
      <Card className="overflow-hidden">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/9028765/pexels-photo-9028765.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Electronic waste and discarded research equipment representing R&D waste management"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h2 className="text-3xl font-bold mb-2">R&D Waste Management</h2>
              <p className="text-lg opacity-90">
                Comprehensive tracking and disposal management for discontinued research assets
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Trash2 className="h-4 w-4 mr-2" />
                  <span>{totalItems} Total Items</span>
                </div>
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-2" />
                  <span>${totalOriginalCost.toLocaleString()} Original Investment</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Last Updated: {lastUpdated}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-red-50 rounded-full mr-3">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-orange-50 rounded-full mr-3">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Broken</p>
                <p className="text-2xl font-bold text-gray-900">{brokenItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-50 rounded-full mr-3">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Discontinued</p>
                <p className="text-2xl font-bold text-gray-900">{discontinuedItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-gray-50 rounded-full mr-3">
                <Trash2 className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Abandoned</p>
                <p className="text-2xl font-bold text-gray-900">{abandonedItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-50 rounded-full mr-3">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Obsolete</p>
                <p className="text-2xl font-bold text-gray-900">{obsoleteItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-50 rounded-full mr-3">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Salvage Value</p>
                <p className="text-lg font-bold text-gray-900">${totalSalvageValue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Impact Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-900">${totalOriginalCost.toLocaleString()}</div>
              <div className="text-sm text-red-600">Total Original Cost</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-900">${totalSalvageValue.toLocaleString()}</div>
              <div className="text-sm text-green-600">Total Salvage Value</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">${(totalOriginalCost - totalSalvageValue).toLocaleString()}</div>
              <div className="text-sm text-gray-600">Net Loss</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-600" />
          </div>
          <Input
            type="search"
            placeholder="Search waste items, project codes, researchers..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </div>
        <Button 
          variant="secondary" 
          className="w-full sm:w-auto"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>
      
      {isFiltersVisible && (
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Select
              label="Category"
              options={categoryOptions}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Waste Type"
              options={statusOptions}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Select
              label="Disposal Status"
              options={disposalOptions}
              value={disposalFilter}
              onChange={(value) => setDisposalFilter(value)}
              fullWidth
            />
          </div>
        </div>
      )}
      
      {filteredItems.length === 0 ? (
        loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Trash2 className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No waste items found</p>
            <Link to="/rd-waste/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Waste Item
              </Button>
            </Link>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredItems.map(item => (
            <RDWasteItemCard
              key={item.id}
              item={item}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RDWasteManagementPage;